import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { get, isEmpty, isEqual } from 'lodash';

import routes from '../../../routes';
import { apiResponseCodes } from '../../../constants/globals';
import asyncRoute from '../../../helpers/hoc/asyncRoute';
import {
  sendOtpAction,
  attemptLoginAction,
  clearGuestFeedbackAction,
} from '../../../actions/app.action';

import TripAdvisorIcon from '../../../assets/images/icons/trip-advisor-icon.svg';
import GoogleIcon from '../../../assets/images/icons/google-icon.svg';
import './FeedbackSuccess.scss';

const Button = asyncRoute(() => import('../../../components/elements/Button'));
const SocialReviewPopup = asyncRoute(() => import('../../../components/Popup/SocialReviewPopup'));
const VerifyNumber = asyncRoute(() => import('../../../components/Popup/VerifyNumber'));
const InvisibleRecaptcha = asyncRoute(() => import('../../../components/InvisibleRecaptcha'));

const FeedbackSuccess = ({
  guestData,
  guestFeedback,
  feedbackSetup,
  brandDeal,
  appStartupInfo,
  history,
  match,
  couponCode,
  actions,
}) => {
  const [showSocialReviewPopup, setShowSocialReviewPopup] = useState(false);
  const [openVerifyNumber, setOpenVerifyNumber] = useState(false);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    const {
      params: { restaurantCode },
    } = match;

    if (isEmpty(guestFeedback)) history.push(`/${routes.containers.feedback}/${restaurantCode}`);

    if (
      get(guestFeedback, 'isShowExtReview', false) &&
      (get(googleReviewLink, 'url') || get(tripAdvisorReviewLink, 'url'))
    ) {
      setShowSocialReviewPopup(true);
    }

    return () => {
      recaptchaRef.current = null;
      actions.clearGuestFeedback();
    };
  }, []);

  const handleViewCouponCTA = async () => {
    const countryCode = get(guestFeedback, 'guest.countryCode', '');
    const phoneNumber = get(guestFeedback, 'guest.primaryPhoneNumber', '');
    const token = await recaptchaRef.current.executeAsync();
    actions.sendOtp(phoneNumber, countryCode, token).then((response) => {
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
        setOpenVerifyNumber(true);
      }
    });
  };

  const handleViewDealCTA = () => {
    history.push(`/${routes.containers.deals}`);
  };

  const handleOpenReviewLink = (reviewLink) => {
    window.open(reviewLink, '_blank').focus();
  };

  const handleVerifyOtp = (otp) => {
    const loginReqBody = {
      phoneNumber: get(guestFeedback, 'guest.primaryPhoneNumber', ''),
      countryCode: get(guestFeedback, 'guest.countryCode', ''),
      otp,
    };
    actions.attemptLogin(loginReqBody).then((response) => {
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
        history.push(`/${routes.containers.coupon}/${couponCode}`);
      }
    });
  };

  const googleReviewLink = get(feedbackSetup, 'additionalData.extFeedbackLinks', []).find(
    (extFeedbackLinks) => get(extFeedbackLinks, 'name') === 'google.review',
  );

  const tripAdvisorReviewLink = get(feedbackSetup, 'additionalData.extFeedbackLinks', []).find(
    (extFeedbackLinks) => get(extFeedbackLinks, 'name') === 'tripadv.review',
  );

  return (
    <>
      {isEqual(get(appStartupInfo, 'brand.status', ''), 'DEMO') && (
        <div className="feedback-success_banner">{'Demo Mode'}</div>
      )}
      <div className="feedback-success_wrapper">
        <div className="feedback-success">
          <div className="feedback-success_brand">
            <img src={get(appStartupInfo, 'brand.logoUrl')} alt="brand-logo" />
          </div>
          <div className="feedback-success_title">Thanks a lot for giving your feedback</div>
          <div className="feedback-success_description">Your love inspires us to keep going</div>
          {get(guestFeedback, 'isShowExtReview', false) &&
            (!isEmpty(get(googleReviewLink, 'url')) ||
              !isEmpty(get(tripAdvisorReviewLink, 'url'))) && (
              <div className="feedback-success_external-review">
                <div className="feedback-success_external-review_description">
                  Please review us on social channels and let the world know about your experience
                </div>
                {!isEmpty(get(googleReviewLink, 'url')) && (
                  <Button
                    variant="outlined"
                    icon={<img src={GoogleIcon} alt="google-icon" />}
                    content="REVIEW US ON GOOGLE"
                    onClick={() => handleOpenReviewLink(get(googleReviewLink, 'url', ''))}
                  />
                )}
                {!isEmpty(get(tripAdvisorReviewLink, 'url')) && (
                  <Button
                    variant="outlined"
                    icon={<img src={TripAdvisorIcon} alt="trip-advisor-icon" />}
                    content="REVIEW US ON TRIPADVISOR"
                    onClick={() => handleOpenReviewLink(get(tripAdvisorReviewLink, 'url', ''))}
                  />
                )}
              </div>
            )}
          <div className="feedback-success_coupon">
            {get(feedbackSetup, 'hasReward') && !get(brandDeal, 'isExpired') ? (
              <>
                <div className="thumbnail">
                  <img src={get(brandDeal, 'dealImageUrl')} alt="thumbnail" />
                </div>
                <div className="description">
                  {`We’ve shared your coupon on 
            ${get(guestData, 'primaryPhoneNumber', '')} and ${get(guestData, 'email', '')}`}
                </div>
                <Button content="VIEW COUPON CODE" onClick={handleViewCouponCTA} />
              </>
            ) : (
              <>
                <div className="description">
                  We’ve might have some interesting deals for your next visit.
                </div>
                <Button content="VIEW DEALS" onClick={handleViewDealCTA} />
              </>
            )}
          </div>
        </div>
        {showSocialReviewPopup && (
          <SocialReviewPopup
            openPopup={showSocialReviewPopup}
            googleReviewLink={get(googleReviewLink, 'url')}
            tripAdvisorReviewLink={get(tripAdvisorReviewLink, 'url')}
            onDismissSegmentPopup={() => setShowSocialReviewPopup(false)}
          />
        )}
        {openVerifyNumber && (
          <VerifyNumber
            openSegmentPopup={openVerifyNumber}
            dismissSegmentPopup={() => setOpenVerifyNumber(false)}
            handleProceedButton={(otp) => handleVerifyOtp(otp)}
            phoneNumber={get(guestFeedback, 'guest.primaryPhoneNumber', '')}
          />
        )}
        <InvisibleRecaptcha recaptchaRef={recaptchaRef} />
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  guestData: get(state, 'app.guestFeedback.guest', {}),
  brandDeal: get(state, 'app.brandDeal', {}),
  feedbackSetup: get(state, 'app.feedbackSetup', {}),
  guestFeedback: get(state, 'app.guestFeedback', {}),
  appStartupInfo: get(state, 'common.appStartupInfo', {}),
  couponCode: get(
    state,
    'app.subscribedDeal.dealGuest.couponCode',
    get(state, 'app.subscribedDeal.couponCode'),
  ),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      attemptLogin: attemptLoginAction,
      sendOtp: sendOtpAction,
      clearGuestFeedback: clearGuestFeedbackAction,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(FeedbackSuccess);
